import clsx from 'clsx'
import { type RefObject } from 'react'

import { ReactComponent as FunnelSvg } from '../../assets/svgs/funnel.svg'
import { PaginationBar } from '../PaginationBar'
import { Spinner } from '../Spinner'
import { ColumnFilterMenuStateManager } from './Filters/ColumnFilterMenuStateManager'
import { ExpertInfoContainer } from '../../pages/painel/Database/DatabaseDetail/components/ExpertInfoContainer'

type RenderMenuFilterProps = {
  isMenuOpen: boolean
  dataTrigger: string
  onCloseMenu: () => void
}

export type Column = {
  field: string
  headerName: string
  isVisible?: boolean
  hasMenuFilter?: boolean
  widthInPercentage?: number
  minWidthInPx?: number
  /** Largura segue o conteúdo (table-layout); use em colunas não fixas quando fizer sentido. */
  fitContent?: boolean
  isSticky?: boolean
  stickyLeftInPx?: number
  maxWidthInPx?: number
  renderCell?: (row: any) => JSX.Element
  renderMenuFilter?: (props: RenderMenuFilterProps) => JSX.Element
}

export type Row = Record<string, any>

type TableProps = {
  columns: Column[]
  rows: Row[]
  isLoading?: boolean
  currentPage?: number
  noContentMessage?: string
  registersPerPage?: number
  totalCountOfRegisters?: number
  perPageOptions?: number[]
  isFetchingNextPage?: boolean
  paginationMode?: 'manual' | 'scroll'
  tableContainerRef?: RefObject<HTMLDivElement>
  onRowClick?: (id: any) => any
  onChangingRegistersPerPage?: (registersPerPage: number) => any
  onPageChange?: (pageNumber: number) => void
  isDashboardMeetingsTable?: boolean
  showExpertBioButton?: boolean
  stripedRows?: boolean
  /** Sobrescreve o fundo listrado da linha (aplique a mesma classe em tr/td sticky). */
  rowClassName?: (row: Row) => string | undefined
  /** Corpo e cabeçalho em 0.75rem (ex.: listagem de Interações). */
  compactListingFont?: boolean
}

export const Table = ({
  columns,
  rows,
  isLoading = false,
  noContentMessage = 'Sem registros',
  currentPage = 1,
  registersPerPage = 10,
  totalCountOfRegisters = 0,
  perPageOptions = [5, 10, 15, 20, 50],
  paginationMode = 'manual',
  tableContainerRef,
  isFetchingNextPage = false,
  onRowClick,
  onChangingRegistersPerPage,
  onPageChange,
  isDashboardMeetingsTable = false,
  showExpertBioButton = false,
  stripedRows = false,
  rowClassName,
  compactListingFont = false,
}: TableProps) => {
  const updatedColumns = columns
    .map((column) => ({
      ...column,
      isSticky: column.isSticky === undefined ? false : column.isSticky,
      isVisible: column.isVisible === undefined ? true : column.isVisible,
    }))
    .filter(({ isVisible }) => isVisible)

  const isEmptyTable = !isLoading && rows.length === 0

  return (
    <>
      <div className="relative w-fit">
        <div
          ref={tableContainerRef}
          className={clsx('w-full', {
            'mb-8 lg:mb-10': paginationMode === 'manual',
            'h-screen':
              !isDashboardMeetingsTable && paginationMode === 'scroll',
            'h-72': isDashboardMeetingsTable && paginationMode === 'scroll',
          })}
        >
          <table
            className={clsx('min-w-full text-left', {
              'text-sm': !compactListingFont,
              'text-[0.75rem]': compactListingFont,
            })}
          >
            <thead>
              <tr>
                {updatedColumns.map(
                  (
                    {
                      headerName,
                      field,
                      widthInPercentage,
                      minWidthInPx,
                      fitContent,
                      hasMenuFilter,
                      isSticky,
                      stickyLeftInPx,
                      renderMenuFilter,
                    },
                    index
                  ) => {
                    const cellKey = `${field}-${index}`
                    const useFitContent =
                      Boolean(fitContent) && widthInPercentage === undefined

                    return (
                      <th
                        key={cellKey}
                        scope="col"
                        className={clsx(
                          'sticky top-0 z-50 px-6 py-3 text-white whitespace-nowrap bg-secondary-dark first-of-type:rounded-l-lg last-of-type:rounded-r-lg',
                          {
                            'z-[70]': isSticky,
                            'text-sm lg:text-base': !compactListingFont,
                            'text-[0.75rem]': compactListingFont,
                          }
                        )}
                        style={{
                          width:
                            widthInPercentage !== undefined
                              ? `${widthInPercentage}%`
                              : useFitContent
                                ? 'max-content'
                                : undefined,
                          minWidth:
                            minWidthInPx !== undefined
                              ? `${minWidthInPx}px`
                              : undefined,
                          left: isSticky ? `${stickyLeftInPx ?? 0}px` : undefined,
                        }}
                      >
                        <div className="relative flex items-center justify-between gap-4">
                          <span title={headerName} className="font-semibold">
                            {headerName}
                          </span>

                          {hasMenuFilter && (
                            <ColumnFilterMenuStateManager>
                              {({ isOpen, onClose, onToggle }) => {
                                return (
                                  <>
                                    <button
                                      onClick={onToggle}
                                      className="inline-flex"
                                    >
                                      <FunnelSvg />
                                    </button>

                                    {renderMenuFilter && (
                                      <div data-trigger={field}>
                                        {renderMenuFilter({
                                          dataTrigger: field,
                                          isMenuOpen: isOpen,
                                          onCloseMenu: onClose,
                                        })}
                                      </div>
                                    )}
                                  </>
                                )
                              }}
                            </ColumnFilterMenuStateManager>
                          )}
                        </div>
                      </th>
                    )
                  }
                )}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length}>
                    <div className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-2/4">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {isEmptyTable ? (
                    <tr>
                      <td colSpan={columns.length}>
                        <div className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-2/4">
                          <p
                            className={clsx(
                              'font-semibold text-center opacity-50 w-60 text-grey-700',
                              compactListingFont ? 'text-[0.75rem]' : 'text-sm'
                            )}
                          >
                            {noContentMessage}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map((row, rowIndex) => {
                        const customRowBg = rowClassName?.(row)
                        const defaultStripeTr =
                          stripedRows && rowIndex % 2 === 0
                            ? 'bg-white'
                            : stripedRows
                              ? 'bg-[#f7fafc]'
                              : undefined
                        const trBg = customRowBg ?? defaultStripeTr
                        const defaultCellBg =
                          !stripedRows || rowIndex % 2 === 0
                            ? 'bg-white'
                            : 'bg-[#f7fafc]'
                        const cellBg = customRowBg ?? defaultCellBg

                        return (
                          <tr
                            key={row.id}
                            className={clsx(
                              'border-b border-grey-500 align-top',
                              trBg
                            )}
                          >
                            {Object.entries(row).map((_, index) => {
                              const column = updatedColumns[index]

                              if (!column || !column.isVisible) {
                                return null
                              }

                              const columnCellValue = row[column.field] || '-'
                              const cellKey = `${column.field}-${index}`
                              const cellFitContent =
                                Boolean(column.fitContent) &&
                                column.widthInPercentage === undefined

                              return (
                                <td
                                  key={cellKey}
                                  className={clsx(
                                    'sticky text-grey-900 z-10 px-6 py-3 overflow-x-hidden whitespace-nowrap text-ellipsis',
                                    cellBg,
                                    {
                                      'z-20': column.isSticky,
                                      'cursor-pointer':
                                        !!onRowClick &&
                                        showExpertBioButton &&
                                        column.field !== 'name',
                                    },
                                    {
                                      'text-sm lg:text-base':
                                        !compactListingFont,
                                      'text-[0.75rem]': compactListingFont,
                                    }
                                  )}
                                  style={{
                                    width: cellFitContent
                                      ? 'max-content'
                                      : undefined,
                                    left: column.isSticky
                                      ? `${column.stickyLeftInPx ?? 0}px`
                                      : undefined,
                                    maxWidth:
                                      column.maxWidthInPx !== undefined
                                        ? `${column.maxWidthInPx}px`
                                        : undefined,
                                  }}
                                  onClick={
                                    showExpertBioButton &&
                                    column.field === 'name'
                                      ? undefined
                                      : () => onRowClick && onRowClick(row.id)
                                  }
                                >
                                  {column.renderCell
                                    ? column.renderCell(row)
                                    : columnCellValue}

                                  {showExpertBioButton &&
                                  column.field === 'name' ? (
                                    <ExpertInfoContainer id={row.id} />
                                  ) : undefined}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>

          {paginationMode === 'scroll' && isFetchingNextPage && (
            <div className="flex items-center justify-center py-4">
              <Spinner size="large" />
            </div>
          )}
        </div>
      </div>

      {paginationMode === 'manual' &&
        onPageChange &&
        onChangingRegistersPerPage && (
          <div className="fixed left-0 bottom-0 bg-white z-40 py-4 w-full">
            <PaginationBar
              currentPage={currentPage}
              onChangingRegistersPerPage={onChangingRegistersPerPage}
              onPageChange={onPageChange}
              registersPerPage={registersPerPage}
              totalCountOfRegisters={totalCountOfRegisters}
              perPageOptions={perPageOptions}
            />
          </div>
        )}
    </>
  )
}
